import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, ShoppingBasket, Trash2 } from 'lucide-react';

import type { ServiceForPicker, ServicesPickerDialogProps } from './types';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

import {
  findInArrayBy,
  pushToArray,
  removeFromArray,
  toMoneyFormat,
} from '@/helpers';

import { ServicesList } from './ServicesList';

type ServicesPickerDialogContentProps = Omit<
  ServicesPickerDialogProps,
  'isOpen'
> & {
  onClose: () => void;
};

const ServicesPickerDialogContent = (
  props: ServicesPickerDialogContentProps,
) => {
  const {
    services,
    onClose,
    onSelect,
    initialSelectedServices = [],
    description,
    showQuantity = false,
    quantityDisabled = false,
  } = props;
  const { t } = useTranslation();

  const [selectedServices, setSelectedServices] = useState<ServiceForPicker[]>(
    initialSelectedServices,
  );
  const [expandedCategory, setExpandedCategory] = useState<string>('');
  const [isHiddenServicesDisplayed, setIsHiddenServicesDisplayed] =
    useState(false);

  const onIsHiddenServicesDisplayedChange = (checked: boolean) => {
    setIsHiddenServicesDisplayed(checked);
  };

  const close = () => {
    onClose();
  };

  const expandCategory = (category: string) => {
    const newExpandedCategory = expandedCategory === category ? '' : category;
    setExpandedCategory(newExpandedCategory);
  };

  const toggleActiveService = (service: ServiceForPicker) => {
    const newSelectedServices = findInArrayBy(selectedServices, service.id)
      ? removeFromArray(selectedServices, service.id)
      : pushToArray(selectedServices, {
          ...service,
          ...(showQuantity && { initial_count: service.initial_count || 1 }),
        });
    setSelectedServices(newSelectedServices);
  };

  const handleDelete = (id: number) => {
    setSelectedServices(removeFromArray(selectedServices, id));
  };

  const handleQuantityChange = (id: number, quantity: number) => {
    if (quantity < 1) return;
    setSelectedServices((prev) =>
      prev.map((s) => (s.id === id ? { ...s, initial_count: quantity } : s)),
    );
  };

  const handleSelect = () => {
    onSelect(selectedServices);
    close();
  };

  const serializedServices = useMemo(() => {
    return services.reduce(
      (res, service) => {
        const { service_category_name, service_category_visible, visible } =
          service;

        const shouldSkip =
          !isHiddenServicesDisplayed && (!visible || !service_category_visible);
        const isCategoryExists =
          Object.keys(res).indexOf(service_category_name || '') > -1;

        if (shouldSkip || isCategoryExists || !service_category_name) {
          return res;
        }

        const filteredServices = services.filter((s) => {
          const matchesCategory =
            s.service_category_name === service_category_name;
          const isVisible = isHiddenServicesDisplayed || s.visible;
          return matchesCategory && isVisible;
        });

        return {
          ...res,
          [service_category_name]: filteredServices,
        };
      },
      {} as Record<string, ServiceForPicker[]>,
    );
  }, [services, isHiddenServicesDisplayed]);

  return (
    <DialogContent className="flex h-[80vh] flex-col sm:max-w-[calc(100%-10rem)]">
      <DialogTitle>{t('Select services')}</DialogTitle>
      {description && <DialogDescription>{description}</DialogDescription>}

      <div className="mb-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="show-hidden-services"
            checked={isHiddenServicesDisplayed}
            onCheckedChange={onIsHiddenServicesDisplayedChange}
          />
          <Label
            htmlFor="show-hidden-services"
            className="cursor-pointer text-sm font-normal"
          >
            {t('Show Hidden Services')}
          </Label>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden md:grid-cols-2">
        <div className="flex flex-col overflow-hidden">
          <Label className="mb-2">{t('Services')}</Label>
          <div className="flex-1 overflow-y-auto rounded-md border p-4">
            <ServicesList
              services={serializedServices}
              selectedServices={selectedServices}
              expandedCategory={expandedCategory}
              onCategoryClick={expandCategory}
              onServiceClick={toggleActiveService}
            />
          </div>
        </div>

        <div className="flex flex-col overflow-hidden">
          <Label className="mb-2">{t('Selected services')}</Label>
          <div className="bg-secondary/10 flex flex-1 flex-col overflow-hidden rounded-md border">
            <div className="flex-1 space-y-1 overflow-y-auto p-2">
              {selectedServices.length === 0 ? (
                <div className="text-muted-foreground flex h-full flex-col items-center justify-center py-12 opacity-50">
                  <ShoppingBasket className="mb-2 size-12" />
                  <p className="text-sm font-medium">
                    {t('No services selected')}
                  </p>
                </div>
              ) : (
                selectedServices.map((service) => {
                  const count = service.initial_count || 1;
                  return (
                    <div
                      key={service.id}
                      className="bg-background animate-in fade-in slide-in-from-right-2 flex items-center gap-2 rounded-lg border px-2 py-1 shadow-sm"
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 size-6 shrink-0"
                        onClick={() => handleDelete(service.id)}
                      >
                        <Trash2 className="size-3" />
                      </Button>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {service.name}
                      </span>
                      {showQuantity && (
                        <div className="flex shrink-0 items-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="size-6"
                            onClick={() =>
                              handleQuantityChange(service.id, count - 1)
                            }
                            disabled={quantityDisabled || count <= 1}
                          >
                            <Minus className="size-3" />
                          </Button>
                          <span className="w-6 text-center text-sm font-medium">
                            {count}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="size-6"
                            onClick={() =>
                              handleQuantityChange(service.id, count + 1)
                            }
                            disabled={quantityDisabled}
                          >
                            <Plus className="size-3" />
                          </Button>
                        </div>
                      )}
                      <span className="text-muted-foreground w-20 shrink-0 text-right text-sm font-semibold">
                        {toMoneyFormat(
                          showQuantity ? service.price * count : service.price,
                        )}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            {selectedServices.length > 0 && (
              <div className="bg-background border-t px-3 py-2 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold">{t('Total')}:</span>
                  <span className="text-xl font-extrabold">
                    {toMoneyFormat(
                      selectedServices.reduce(
                        (sum, s) => sum + s.price * (s.initial_count || 1),
                        0,
                      ),
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={close}
        >
          {t('Close')}
        </Button>
        <Button
          type="button"
          onClick={handleSelect}
        >
          {t('Select')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export const ServicesPickerDialog = (props: ServicesPickerDialogProps) => {
  const { isOpen, onClose, ...contentProps } = props;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
    >
      {isOpen && (
        <ServicesPickerDialogContent
          {...contentProps}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};
